"use client";

import { useT } from "@/hooks/useT";

const FEATURE_KEYS = [
  { titleKey: "features.f1.title", highlightKey: "features.f1.highlight", descKey: "features.f1.desc", iconIdx: 0 },
  { titleKey: "features.f2.title", highlightKey: "features.f2.highlight", descKey: "features.f2.desc", iconIdx: 1 },
  { titleKey: "features.f3.title", highlightKey: null, descKey: "features.f3.desc", iconIdx: 2 },
  { titleKey: "features.f4.title", highlightKey: null, descKey: "features.f4.desc", iconIdx: 3 },
] as const;

const ICONS = [
  (
    <svg className="w-14 h-14 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  ),
  (
    <svg className="w-14 h-14 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  ),
  (
    <svg className="w-14 h-14 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  ),
  (
    <svg className="w-14 h-14 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
    </svg>
  ),
] as const;

export default function FeaturesSection() {
  const t = useT();
  const features = FEATURE_KEYS.map(f => ({
    title: t(f.titleKey),
    highlight: f.highlightKey ? t(f.highlightKey) : null,
    description: t(f.descKey),
    icon: ICONS[f.iconIdx],
  }));
  return (
    <section
      id="features"
      className="relative bg-white dark:bg-[#0f0f12] py-12 sm:py-28 px-4 sm:px-8 lg:px-16"
    >
      <div className="mx-auto mb-10 sm:mb-20 text-center max-w-7xl">
        <p className="text-accent-primary font-monument font-semibold uppercase tracking-wider text-sm">
          {t("features.eyebrow")}
        </p>
        <h2 className="text-4xl sm:text-5xl font-monument font-extrabold text-gray-900 dark:text-white mt-2 leading-tight">
          {t("features.headline.before")}{" "}
          <span className="text-accent-primary">
            Block 0
          </span>{" "}
          {t("features.headline.after")}
        </h2>
        <p className="mt-4 sm:mt-6 text-gray-600 dark:text-gray-400 max-w-2xl mx-auto text-base sm:text-lg font-satoshi">
          {t("features.subtitle")}
        </p>
      </div>

      {/* Mobile Slider */}
      <div className="sm:hidden">
        <div className="flex px-1 pb-4 -mx-4 space-x-4 overflow-x-auto snap-x snap-mandatory">
          {features.map((feature, idx) => (
            <div
              key={idx}
              className="snap-center flex-shrink-0 w-[85%] bg-white dark:bg-[#18181D] rounded-[5px] p-6 text-center border border-gray-200 dark:border-[#1A1A1A]"
            >
              <div className="mb-4 text-gray-500 dark:text-gray-400">
                {feature.icon}
              </div>
              <h3 className="text-lg font-monument font-semibold text-gray-900 dark:text-white">{feature.title}</h3>
              {"highlight" in feature && feature.highlight && (
                <p className="mt-1 text-sm font-semibold text-accent-primary">{feature.highlight}</p>
              )}
              <p className="mt-2 text-gray-600 dark:text-gray-400 text-sm font-satoshi">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Desktop Grid */}
      <div className="hidden grid-cols-2 gap-12 mx-auto sm:grid lg:grid-cols-4 max-w-7xl">
        {features.map((feature, idx) => (
          <div key={idx} className="text-center">
            <div className="mb-6 text-gray-500 dark:text-gray-400">
              {feature.icon}
            </div>
            <h3 className="text-xl font-monument font-semibold text-gray-900 dark:text-white">{feature.title}</h3>
            {"highlight" in feature && feature.highlight && (
              <p className="mt-1 text-sm font-semibold text-accent-primary">{feature.highlight}</p>
            )}
            <p className="mt-2 text-gray-600 dark:text-gray-400 text-sm font-satoshi">{feature.description}</p>
          </div>
        ))}
      </div>
    </section>
  );
}
